import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const PRODUCT_KINDS = ["item", "combo"] as const;
const PREPARATION_TYPES = ["prepared", "on_demand"] as const;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type ComboItemInput = {
  productId?: string;
  productName?: string;
  quantity: number;
};

export const createProductSkill: Skill = {
  id: "createProduct",
  description:
    "Crea un nuevo producto (individual o combo) en el catálogo de un restaurante. " +
    "Úsala cuando el usuario pida crear/agregar un producto. SIEMPRE confirma con el usuario " +
    "los datos clave antes de invocar. Para COMBOS: debes incluir `comboItems` con al menos 1 componente " +
    "(productos existentes en el catálogo). Si el usuario no te dice los componentes, pregúntale.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      name: { type: "string", description: "Nombre del producto" },
      price: {
        type: "number",
        description: "Precio (en la moneda local del restaurante).",
      },
      productKind: {
        type: "string",
        enum: [...PRODUCT_KINDS],
        description:
          "'item' = producto individual (default). 'combo' = compuesto por otros productos del catálogo.",
      },
      preparationType: {
        type: "string",
        enum: [...PREPARATION_TYPES],
        description:
          "Modo de preparación. 'prepared' = listo/no pasa por cocina (default). 'on_demand' = requiere cocina. Aplica también a combos.",
      },
      description: {
        type: "string",
        description: "Descripción opcional.",
      },
      categoryId: {
        type: "string",
        description: "ID de categoría opcional.",
      },
      comboItems: {
        type: "array",
        description:
          "OBLIGATORIO si productKind='combo'. Lista de componentes del combo (productos existentes). " +
          "Pasa `productId` (preferido) o `productName` (se resuelve por coincidencia exacta case-insensitive) " +
          "y `quantity` (entero ≥ 1). No se admiten combos dentro de combos.",
        items: {
          type: "object",
          properties: {
            productId: { type: "string" },
            productName: { type: "string" },
            quantity: { type: "integer", minimum: 1 },
          },
          required: ["quantity"],
        },
      },
    },
    required: ["restaurantId", "name", "price"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const name = String(args.name ?? "").trim();
    const price = Number(args.price);
    if (!restaurantId || !name || !Number.isFinite(price) || price < 0) {
      return {
        ok: false,
        error: "Faltan datos válidos: restaurantId, name, price >= 0.",
      };
    }

    // ── Resolución de tipo (acepta valores nuevos y legacy) ─────────────────
    // Legacy: el LLM podría seguir pasando `type: "prepared"|"on_demand"`.
    // Lo aceptamos y mapeamos a productKind=item + preparationType=ese valor.
    let productKind = (args.productKind as string) || "item";
    let preparationType =
      (args.preparationType as string) ||
      // legacy fallback
      (args.type === "on_demand" ? "on_demand" : "prepared");

    // Si el LLM (legacy) pasó type="combo", reconciliar
    if (args.type === "combo") productKind = "combo";

    if (!PRODUCT_KINDS.includes(productKind as (typeof PRODUCT_KINDS)[number])) {
      return { ok: false, error: `productKind inválido: ${productKind}` };
    }
    if (
      !PREPARATION_TYPES.includes(
        preparationType as (typeof PREPARATION_TYPES)[number],
      )
    ) {
      return { ok: false, error: `preparationType inválido: ${preparationType}` };
    }

    const isCombo = productKind === "combo";

    // ── Validación de comboItems (si combo) ─────────────────────────────────
    let resolvedComboItems: Array<{ productId: string; name: string; quantity: number }> = [];
    if (isCombo) {
      const rawItems = Array.isArray(args.comboItems)
        ? (args.comboItems as ComboItemInput[])
        : [];
      if (rawItems.length === 0) {
        return {
          ok: false,
          error:
            "Un combo requiere al menos 1 componente. Pasa `comboItems: [{ productId|productName, quantity }]`.",
        };
      }

      // Cargar productos del restaurante para resolver nombres y validar.
      let catalog: Array<{ id: string; name: string; type?: string; isArchived?: boolean }> = [];
      try {
        const { data } = await apiWithAuth.get(
          `/restaurants/${restaurantId}/products?limit=500&includeArchived=false`,
          ctx.accessToken,
        );
        catalog = (data?.data ?? data?.items ?? data ?? []) as typeof catalog;
      } catch (e: any) {
        return {
          ok: false,
          error:
            "No se pudo cargar el catálogo para resolver componentes: " +
            (e?.message ?? "error desconocido"),
        };
      }

      const byId = new Map(catalog.map((p) => [p.id, p]));
      const byName = new Map(
        catalog.map((p) => [p.name.trim().toLowerCase(), p]),
      );

      const errors: string[] = [];
      for (const raw of rawItems) {
        const qty = Math.max(1, Math.floor(Number(raw.quantity)));
        if (!Number.isFinite(qty) || qty < 1) {
          errors.push(`Cantidad inválida en componente.`);
          continue;
        }
        let match: { id: string; name: string; type?: string } | undefined;
        if (raw.productId) {
          match = byId.get(String(raw.productId));
          if (!match) {
            errors.push(`Componente productId="${raw.productId}" no existe.`);
            continue;
          }
        } else if (raw.productName) {
          match = byName.get(String(raw.productName).trim().toLowerCase());
          if (!match) {
            errors.push(
              `Componente "${raw.productName}" no se encontró por nombre exacto.`,
            );
            continue;
          }
        } else {
          errors.push("Componente sin productId ni productName.");
          continue;
        }
        if (match.type === "combo") {
          errors.push(
            `"${match.name}" es un combo. No se permiten combos dentro de combos.`,
          );
          continue;
        }
        resolvedComboItems.push({
          productId: match.id,
          name: match.name,
          quantity: qty,
        });
      }
      if (errors.length > 0) {
        return { ok: false, error: errors.join(" ") };
      }
    }

    // ── Anti-duplicado por nombre ────────────────────────────────────────
    try {
      const { data: existing } = await apiWithAuth.get(
        `/restaurants/${restaurantId}/products?search=${encodeURIComponent(name)}&limit=10`,
        ctx.accessToken,
      );
      const items: any[] = existing?.items ?? existing?.data ?? existing ?? [];
      const exact = items.find(
        (p) => String(p.name).trim().toLowerCase() === name.toLowerCase(),
      );
      if (exact) {
        return {
          ok: false,
          error: `Ya existe un producto llamado "${exact.name}" (precio actual $${exact.price}). Si quieres modificarlo usa updateProduct/updateProductPrice. Si quieres uno distinto, dale otro nombre.`,
        };
      }
    } catch {
      // Si falla la pre-check, intentamos crear igualmente
    }

    // ── Confirmación bloqueante ─────────────────────────────────────────
    const details: Array<{ label: string; value: string }> = [
      { label: "Nombre", value: name },
      { label: "Precio", value: `$${price.toLocaleString()}` },
      { label: "Tipo", value: isCombo ? "Combo" : "Producto" },
      {
        label: "Modo",
        value:
          preparationType === "prepared"
            ? "Preparado (no pasa por cocina)"
            : "Bajo pedido (pasa por cocina)",
      },
    ];
    if (args.description) {
      details.push({ label: "Descripción", value: String(args.description) });
    }
    if (isCombo) {
      details.push({
        label: "Incluye",
        value: resolvedComboItems
          .map((i) => `${i.quantity}× ${i.name}`)
          .join(", "),
      });
    }

    const ok = await ctx.confirmation.confirm({
      title: isCombo ? "Crear combo" : "Crear producto",
      description: isCombo
        ? "Vas a crear un combo compuesto por productos existentes. El stock se calculará automáticamente desde sus componentes."
        : "Vas a crear un nuevo producto en el catálogo.",
      details,
      confirmLabel: "Crear",
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    // ── Visual narrative en follow mode (tutorial) ─────────────────────
    if (ctx.follow.enabled) {
      await ctx.follow.step(
        `Voy a crear ${isCombo ? "el combo" : "el producto"} "${name}" — sígueme`,
        { durationMs: 900 },
      );

      // 1. Click en tab Productos (sidebar)
      await ctx.follow.clickAt(`[data-copilot-tab="productos"]`, {
        durationMs: 700,
        label: "Productos",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=productos`,
      );
      await sleep(1100); // esperar render del tab

      // 2. Click en "+ Nuevo producto"
      await ctx.follow.step(
        "Aquí está el botón para crear un producto nuevo",
        { durationMs: 600 },
      );
      await ctx.follow.clickAt(`[data-copilot-action="create-product"]`, {
        durationMs: 700,
        label: "Nuevo producto",
      });
      await sleep(900); // que aparezca el modal

      // 3. Narrar llenado
      await ctx.follow.step(
        `Llenando datos: ${name} · $${price.toLocaleString()}`,
        { durationMs: 1200 },
      );

      // 4. Narrar confirmación
      await ctx.follow.step("Guardando…", { durationMs: 500 });
    }

    // ── API call ────────────────────────────────────────────────────────
    try {
      const body: Record<string, unknown> = {
        name,
        price,
        type: isCombo ? "combo" : preparationType,
        preparationType,
      };
      if (args.description) body.description = String(args.description);
      if (args.categoryId) body.categoryId = String(args.categoryId);
      if (isCombo) {
        body.comboItems = resolvedComboItems.map((i, idx) => ({
          productId: i.productId,
          quantity: i.quantity,
          sortOrder: (idx + 1) * 10,
        }));
      }

      const { data } = await apiWithAuth.post(
        `/restaurants/${restaurantId}/products`,
        body,
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step(
          `✓ ${isCombo ? "Combo" : "Producto"} "${name}" creado`,
          {
            durationMs: 1100,
            spinner: false,
          },
        );
        ctx.follow.clear();
      }

      // Aviso al tab para refetch + scroll al producto nuevo
      emitCopilotMutation({
        scope: "products",
        action: "created",
        restaurantId,
        id: data?.id,
        meta: { name },
      });

      return {
        ok: true,
        result: {
          id: data?.id,
          name: data?.name ?? name,
          price: data?.price ?? price,
          productKind,
          preparationType,
          ...(isCombo
            ? {
                comboItems: resolvedComboItems.map((i) => ({
                  productId: i.productId,
                  name: i.name,
                  quantity: i.quantity,
                })),
              }
            : {}),
        },
        humanSummary: isCombo
          ? `Combo "${name}" creado ($${price}) con ${resolvedComboItems.length} componente(s).`
          : `Creado: ${name} ($${price}).`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step(
          `✗ Error al crear ${isCombo ? "combo" : "producto"}`,
          {
            durationMs: 700,
            spinner: false,
          },
        );
        ctx.follow.clear();
      }
      const detail =
        err?.response?.data?.message ||
        err?.message ||
        "Error creando producto.";
      return { ok: false, error: detail };
    }
  },
};
